import Transfer from '@/model/transfer.mjs';
import Licensee from '@/model/licensee.mjs';
import { IGetRowsParams } from 'ag-grid-community';

export enum LastMessageStatusEnum {
  sent,
  delivered,
  error,
  bounced,
  opened
}

enum CertificateWorkflowStatusStep {
  license_image_optimizer,
  ocr_check,
  license_validator,
  ez_validator
}

export const certificateWorkflowStatusStep = {
  license_image_optimizer: 'license image optimizer',
  ocr_check: 'OCR check',
  license_validator: 'license validator',
  ez_validator: 'EZ validator'
};

export type certificateWorkflowStatusStepKey =
  keyof typeof certificateWorkflowStatusStep;
export interface TransferDetailsType {
  TransferDetails: {
    message: {
      usedEmail: string;
      lastMessageStatus: LastMessageStatusEnum | null;
      lastMessageSent: number | null;
    };
    certificateWorkflowStatus: {
      step: CertificateWorkflowStatusStep | null;
      status: string;
      time: number | null;
    };
    optChoice: {
      value: string;
      time: number | null;
    };
    products: Array<string> | null;
    status: string;
    buyer: {
      street: string | null;
      city: string | null;
      state: string | null;
      zipCode: string | null;
      name: string;
      phone: string;
      email: string;
    };
    orderId: string;
    sku: string;
    transfer_proof_link?: string;
    transfer_proof_path?: string;
    transferSellerPacket?: string;
    notes: string;
    orderDate: string;
    storeURL: string | null;
    modelType: string;
    shippingAddress: {
      state: string;
      street: string;
      city: string;
      zipCode: string;
    } | null;
    transferId: string;
    isArchive?: boolean;
    agentName: string;
    agentEmail: string;
    transferType: string | null;
    transferCode: string;
    microStatus: string | null;
    numberOfDealerAssgined: number;
    createdAt?: string;
    outboundTransferCompletionTimestamp?: string;
    version: number;
    requestId?: string;
  };
  fflMasterDetails: {
    licenseId: string;
    contactName: Array<string> | null;
    businessType: string;
    email: Array<string> | [];
    contactNumber: Array<string> | [];
    businessName: string | null;
  };
  SellerDetails: {
    licenseId: string;
    /** Live store vs testing store (matches queue `is_external` / Live–Testing column). */
    is_external?: boolean;
  };
}

export type keyType<T> = keyof T;

export interface TransferEmailHistory {
  event: string;
  createdAt: number;
}

export interface TransferEmailHistoryProps {
  emailStatusHistory: Array<TransferEmailHistory>;
}

export interface TransferStatusWorkflow {
  status: string;
  microStatus: Array<TransferMicroStatusWorkflow>;
  order: number;
  stepStatus: string;
  startedAt: string;
  finishedAt: string;
  source?: keyType<typeof TransferSourceEnum>;
  sourceDescription?: string;
}

export interface InBoundCustomerPickUp {
  buttonText?: string | null;
  isActive: boolean;
}

export interface TransferMicroStatusWorkflow {
  status: TransferMicrostatus;
  createdAt: string;
  metaData: MicroStatusMetaData | null;
}

interface MicroStatusMetaData {
  timestamp?: string;
  value?: string;
  dealer_name?: string;
  license?: string;
  email?: string;
  status?: string;
  approved?: boolean;
  reviewed_by?: string;
  code?: number;
  createdBy?: string;
  optChoice?: string;
  type?: string;
  documentType?: string;
  actionPerformedBy?: string;
  reason?: any;
  reasons?: string[];
  result?: string;
  action?: string;
  resume_context?: string;
  createdby?: string;
  created_by?: string
  source?: string
}

export enum FilterMode {
  LICENSE_FILTER,
  TRANSFER_FILTER
}

export interface TransferFilterModel {
  transferType: FilterItem;
  storeName: FilterItem;
  source: FilterItem,
  derivedStatus: FilterItem;
  agent: FilterItem;
  firearm : FilterItem;
  'licensee.id': FilterText;
  referenceId: FilterText;
  sellerLicenseKey: FilterText | FilterItem;
  searchText: FilterText;
  microStatus: FilterItem;
  createdAt: DateRangeFilterType;
  is_external:FilterItem;
  isArchive:FilterItem;
  ecommerce_status:FilterItem;
  linkedTransfers: FilterItem;
}

interface DateRangeFilterType {
  dateFrom: string | null;
  dateTo: string | any | null;
  filterType: string;
  type: string;
}
// {filterType: "date", type: "inRange", dateFrom: "", dateTo: ""}
export interface FilterItem {
  filterType: string;
  values: Array<string>;
  type?: string;
}

export interface FilterText {
  filter: string;
  filterType: string;
  type: string;
  value?: string | null;
}

export enum TransferErrorMicrostatus {
  opted_out = 'opted_out',
  email_not_found = 'email_not_found',
  email_bounced = 'email_bounced',
  license_acquisition_failed = 'license_acquisition_failed',
  license_validation_failed = 'license_validation_failed',
  upload_license = 'upload_license'
}

export enum TransferMicrostatus {
  waiting_for_mturk = 'waiting_for_mturk',
  waiting_for_admin = 'waiting_for_admin',
  opted_out = 'opted_out',
  email_not_found = 'email_not_found',
  email_delivered = 'email_delivered',
  email_clicked = 'email_clicked',
  email_opened = 'email_opened',
  email_bounced = 'email_bounced',
  accepted_transfer = 'accepted_transfer',
  running_ocr_check = 'running_ocr_check',
  running_ez_check = 'running_ez_check',
  license_validated = 'license_validated',
  license_acquisition_failed = 'license_acquisition_failed',
  license_validation_failed = 'license_validation_failed',
  upload_license = 'upload_license',
  dealer_unresponsive = 'dealer_unresponsive',
  receiving_ffl_click = 'receiving_ffl_click',
  reject_terms_and_condition = "reject_terms_and_condition",
  accept_terms_and_condition = "accept_terms_and_condition",
  waiting_for_terms_and_condition = "waiting_for_terms_and_condition",
  dealer_selected = "dealer_selected",
  resent_email = "resent_email",
  customer_selected = "customer_selected",
  ez_check = 'ez_check',
  sot_tax_stamp_validated = 'sot_tax_stamp_validated',
  loa_certificate_validated = 'loa_certificate_validated',
  ocr_checks = 'ocr_checks',

  // transfer_v2 new micro status
  completed = 'completed',
  in_hold = 'in_hold',
  in_progress = 'in_progress',
  email_available = 'email_available',
  email_acquired = 'email_acquired',
  waiting_for_customer_action = 'waiting_for_customer_action',
  waiting_for_agent_action = 'waiting_for_agent_action',
  dealer_action_pending = 'dealer_action_pending',
  required_documents_pending = 'required_documents_pending',
  dealer_documents_pending = 'dealer_documents_pending',
  document_validated = 'document_validated',
  manual_license_review = 'manual_license_review',
  email_reminder_sent= 'email_reminder_sent',
  ready_for_outbound_processing = 'ready_for_outbound_processing',
  manual_review_pending = 'manual_review_pending',
  sms_sent_to_dealer = 'sms_sent_to_dealer',
  transfer_initiated = 'transfer_initiated',
  required_documents_available = 'required_documents_available',
  document_available = 'document_available',
  document_uploaded = 'document_uploaded',
  first_time_confirmation_delay = 'first_time_confirmation_delay',
  skip_step_acquired = 'skip_step_acquired',
  email_unreachable= "email_unreachable",
  transfer_resume_attempted = 'transfer_resume_attempted'
}

export enum TransferMacrostatus {
  transfer_initiated = 'transfer_initiated',
  waiting_for_email = 'waiting_for_email',
  initiation_email_sent = 'initiation_email_sent',
  waiting_dealer_action = 'waiting_dealer_action',
  waiting_license_validation = 'waiting_license_validation',
  outbound_transfer_confirmed = 'outbound_transfer_confirmed',
  outbound_transfer_complete = 'outbound_transfer_complete',
  tranfer_pending = 'tranfer_pending'
}

export interface createTransferData {
  id: string;
}

export interface TransferListCustomParams extends IGetRowsParams {
  showPendingTransfers: boolean;
  isArchive: boolean;
  type?: string
  isInbound?: boolean
}
export interface CreateTransferPayload
  extends Omit<
    Transfer,
    | 'street'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'buyerPhone'
    | 'buyerEmail'
    | 'buyerName'
    | 'key'
    | 'referenceId'
  > {
  buyer: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phone: string;
    email: string;
    name: string;
  };
  licenseKey: string;
  orderId: string;
}

export interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  licensee?: Licensee;
  refetchData?: () => void;
}

export enum TransferFormMode {
  TRANSFER_EDIT = 'TRANSFER_EDIT'
}

export interface TransferListResquestParams {
  'orderBy[]'?: string;
  'filter[]'?: string;
  startRow?: string;
  pageSize?: string;
  onlyMyTransfers?: string;
  showPendingTransfers?: string;
  search?: string;
}
export enum TransferCurrentNavigation {
  TRANSFER_QUEUE,
  ARCHIVE
}

export enum Toastertype {
  success = 'success',
  error = 'error'
}

export enum TransferTypeInTransferList {
  out_bound = 'Outbound',
  in_bound = 'Inbound'
}
export interface BlobType extends Blob {
  name: string;
}

export interface TransferTypes{
    in_bound: string;
    out_bound: string;
}

export enum TransferSourceEnum {
  FFL_360 = 'FFL 360',
  FFL_SELECT = 'E-commerce',
  WOO_COMMERCE = 'Woo-commerce',
  BIG_COMMERCE = 'Big-commerce',
  ONLINE = 'Online',
  API = 'E-commerce'
}
